import {Clear, Check} from '@mui/icons-material';
import {
  Dialog,
  Stack,
  DialogTitle,
  Typography,
  Box,
  IconButton,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
} from '@mui/material';
import {selectProcessModelSelections} from 'app/store/appState/appStateSlice';
import {selectProcessesWithMultipleModels} from 'app/store/operatingModel/operatingModelSlice';
import React from 'react';
import {useSelector} from 'react-redux';

type ResourceModelSelectionProps = {
  processTitle: string;
  open: boolean;
  onConfirm?: (model: string) => void;
  onClose?: () => void;
};

const ResourceModelSelection = ({processTitle, open, onConfirm, onClose}: ResourceModelSelectionProps) => {
  const theme = useTheme();
  const processWithMultipleModels = useSelector(selectProcessesWithMultipleModels);
  const processModelSelections = useSelector(selectProcessModelSelections);

  const getInitialModelState = () => {
    const process = processModelSelections?.find((p) => p.process_name === processTitle);
    if (process) {
      return process.model;
    } else {
      return '';
    }
  };

  const [openState, setOpenState] = React.useState(open);
  const [selectedModel, setSelectedModel] = React.useState(getInitialModelState());

  const getAllModels = () => {
    return processWithMultipleModels
      ?.filter((p) => p.name === processTitle)
      .map((m) => m.model)
      .sort((a, b) => {
        if (a && b) {
          if (a > b) {
            return 1;
          } else {
            return -1;
          }
        } else return 0;
      });
  };

  React.useEffect(() => {
    if (openState !== open) setOpenState(open);

    if (!selectedModel && processModelSelections?.map((p) => p.process_name)?.includes(processTitle)) {
      const process = processModelSelections?.find((p) => p.process_name === processTitle);
      if (process) {
        setSelectedModel(process.model);
      }
    }
  }, [open, openState, processModelSelections, selectedModel, processTitle]);

  const handleClose = () => {
    setOpenState(false);
    setSelectedModel('');
    onClose && onClose();
  };
  return (
    <Dialog
      open={openState}
      aria-labelledby='popup-dialog'
      BackdropProps={{invisible: true}}
      disableScrollLock
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '400px',
          width: '100%',
          margin: '20vh auto', // Centering vertically, 20% from top
        },
      }}
      PaperProps={{
        sx: {
          maxWidth: '450px',
          backgroundColor: 'grey.200',
          boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.25)',
        },
      }}
    >
      <Stack sx={{justifyContent: 'space-between'}} direction='row'>
        <DialogTitle id='confirm-dialog' sx={{px: 2}}>
          <Typography variant='h1' color='grey.800' padding={0}>
            Choose a resourcing model
          </Typography>
        </DialogTitle>
        <Box>
          <IconButton sx={{pb: 0}} disableRipple onClick={() => handleClose()}>
            <Clear fontSize='small' sx={{color: theme.palette.grey[600]}} />
          </IconButton>
        </Box>
      </Stack>
      <DialogContent sx={{p: 0}}>
        <Box sx={{px: 2, pb: '5px', pt: '5px'}}>
          <Stack direction='column' spacing={'10px'}>
            <Typography variant='h6'>
              <b>
                <i>{processTitle}</i>
              </b>{' '}
              can be performed with multiple resourcing models:
            </Typography>
            <Typography variant='h6'>
              <b>Insourced</b>: Takeda is solely responsible and accountable for the execution of the process.
            </Typography>
            <Typography variant='h6'>
              <b>Outsourced</b>: CRO is solely responsible and accountable for the execution of the process.
            </Typography>
            <Typography variant='h6'>
              <b>Hybrid</b>: Takeda and CRO share responsibility and accountability for the execution of the process.
            </Typography>
            <Typography variant='h6'>{`If you’re unsure which model to choose, talk with your study team.`}</Typography>
          </Stack>
        </Box>
        <Box sx={{width: '100%', px: 2, py: 1}}>
          <Box sx={{width: '100%', borderBottom: `1px solid ${theme.palette.grey[600]}`}} />
        </Box>
        <Typography py={1} px={2} variant='h6'>
          Select the model you’d like to view:
        </Typography>
        <Stack px={2} direction='row' spacing='5px' alignItems='center'>
          {getAllModels()?.map((item) => (
            <Box
              key={item}
              sx={{
                p: '10px',
                maxHeight: '26px',
                minHeight: '26px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                bgcolor: 'white',
                border:
                  selectedModel === item
                    ? `1.5px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.grey[500]}`,
                '&:hover': {
                  bgcolor: theme.palette.grey[100],
                  cursor: 'pointer',
                  border: `1px solid ${theme.palette.grey[500]}`,
                },
              }}
              onClick={() => {
                item && setSelectedModel(item);
              }}
            >
              <Stack direction='row' spacing='10px'>
                {selectedModel === item && <Check fontSize='small' sx={{color: theme.palette.primary.main}} />}
                <Typography variant='h5' color={selectedModel === item ? theme.palette.primary.main : 'inherit'}>
                  {item}
                </Typography>
              </Stack>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{p: '20px', justifyContent: 'flex-end'}}>
        <Button size='small' onClick={() => handleClose()} sx={{color: theme.palette.primary.main}}>
          cancel
        </Button>
        <Button
          variant='contained'
          size='small'
          onClick={() => {
            setOpenState(false);
            onConfirm && onConfirm(selectedModel);
          }}
          sx={{bgcolor: theme.palette.primary.main}}
          disabled={selectedModel ? false : true}
        >
          Go To Process
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResourceModelSelection;
